// ============================================================================
// SliderSelector.jsx — Corelia v1.1
// Slider discret à niveaux configurables
// - Support 3 à 5 niveaux (ou plus si nécessaire)
// - Labels courts au-dessus du slider
// - Label long contextuel à droite selon le niveau sélectionné
// - Piloté par une constante "levels"
// - Robuste : fallback, clamp, accessibilité, disabled
// ============================================================================

import React, { useMemo, useId } from "react";
import theme from "../../../styles/theme";
import FormLayout from "./FormLayout";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const normalizeLevels = (levels) => {
  const fallback = [
    { value: 1, shortLabel: "Low", longLabel: "Low level" },
    { value: 2, shortLabel: "Medium", longLabel: "Medium level" },
    { value: 3, shortLabel: "High", longLabel: "High level" },
  ];

  if (!Array.isArray(levels)) return fallback;

  const cleaned = levels
    .map((item, index) => ({
      value: index + 1, // on force 1..n pour rester cohérent avec le slider
      shortLabel: item?.shortLabel != null ? String(item.shortLabel) : `Level ${index + 1}`,
      longLabel:
        item?.longLabel != null
          ? String(item.longLabel)
          : item?.shortLabel != null
          ? String(item.shortLabel)
          : `Level ${index + 1}`,
    }))
    .slice(0, 5);

  if (cleaned.length < 3) return fallback;

  return cleaned;
};

const SliderSelector = ({
  label,
  labelPosition = "left",
  labelWidth = "130px",
  align = "center",
  longLabelOffset = 12,

  value,
  onChange,

  levels = null, // ex: [{ value:1, shortLabel:"Low", longLabel:"Low maturity" }, ...]
  disabled = false,
  width = "100%",
  style = {},

  showLongLabel = true,
  longLabelWidth = "180px",
}) => {
  const reactId = useId();
  const inputId = `corelia-slider-${reactId}`;

  const normalizedLevels = useMemo(() => normalizeLevels(levels), [levels]);

  const minV = 1;
  const maxV = normalizedLevels.length;

  const safeValue = useMemo(() => {
    const raw = Number(value);
    if (!Number.isFinite(raw)) return minV;

    const allowedValues = normalizedLevels.map((item) => item.value);

    if (allowedValues.includes(raw)) {
      return clamp(raw, minV, maxV);
    }

    // fallback vers la valeur la plus proche
    let closest = allowedValues[0];
    let minDiff = Math.abs(raw - closest);

    for (let i = 1; i < allowedValues.length; i += 1) {
      const diff = Math.abs(raw - allowedValues[i]);
      if (diff < minDiff) {
        minDiff = diff;
        closest = allowedValues[i];
      }
    }

    return clamp(closest, minV, maxV);
  }, [value, normalizedLevels, minV, maxV]);

  const currentIndex = useMemo(
    () => normalizedLevels.findIndex((item) => item.value === safeValue),
    [normalizedLevels, safeValue]
  );

  const currentLevel =
    currentIndex >= 0 ? normalizedLevels[currentIndex] : normalizedLevels[0];

  const handleChange = (e) => {
    if (!onChange) return;

    const nextValue = Number(e.target.value);
    if (!Number.isFinite(nextValue)) {
      onChange(null);
      return;
    }

    onChange(nextValue);
  };

  return (
    <FormLayout
      label={label}
      labelPosition={labelPosition}
      labelWidth={labelWidth}
      align={align}
      width={width}
      style={style}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          gap: longLabelOffset,
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* labels courts au-dessus */}
          <div
            style={{
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: 6,
            }}
          >
            {normalizedLevels.map((item) => {
              const isActive = item.value === safeValue;

              return (
                <div
                  key={item.value}
                  title={item.longLabel}
                  style={{
                    flex: 1,
                    textAlign: "center",
                    fontFamily: theme.form.label.fontFamily,
                    fontSize: theme.fontSizes.xs,
                    color: isActive
                      ? theme.colors.text.corelia
                      : theme.colors.text.small,
                    fontWeight: isActive
                      ? theme.fontWeights.medium
                      : theme.fontWeights.normal,
                    lineHeight: 1.2,
                    userSelect: "none",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {item.shortLabel}
                </div>
              );
            })}
          </div>

          {/* slider */}
          <input
            id={inputId}
            type="range"
            min={minV}
            max={maxV}
            step={1}
            value={safeValue}
            onChange={handleChange}
            disabled={disabled}
            aria-label={label || "Slider"}
            list={`${inputId}-datalist`}
            style={{
              width: "100%",
              margin: 0,
              accentColor: theme.colors.text.corelia,
            }}
          />

          <datalist id={`${inputId}-datalist`}>
            {normalizedLevels.map((item) => (
              <option key={item.value} value={item.value} />
            ))}
          </datalist>
        </div>

        {/* label long à droite */}
        {showLongLabel && (
          <div
            title={currentLevel.longLabel}
            style={{
              width: longLabelWidth,
              minWidth: longLabelWidth,
              fontFamily: theme.form.label.fontFamily,
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.small,
              lineHeight: 1.35,
              paddingTop: 18,
              whiteSpace: "normal",
              wordBreak: "break-word",
             
            }}
          >
            {currentLevel.longLabel}
          </div>
        )}
      </div>
    </FormLayout>
  );
};

export default SliderSelector;